import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

interface FeatureFlagContextType {
    flags: string[];
    isLoaded: boolean;
    hasFlag: (flagId: string) => boolean;
    hasAnyFlag: (flagIds: string[]) => boolean;
    refreshFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const useFeatureFlags = () => {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
    }
    return context;
};

export const useFeatureFlag = (flagId: string): boolean => {
    const { hasFlag } = useFeatureFlags();
    return hasFlag(flagId);
};

const STORAGE_KEY = 'hofsync_feature_flags';

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const [flags, setFlags] = useState<string[]>(() => {
        // Hydrate from localStorage for fast initial render
        try {
            const cached = localStorage.getItem(STORAGE_KEY);
            return cached ? JSON.parse(cached) : [];
        } catch {
            return [];
        }
    });
    // If we have cached flags, consider them loaded immediately to prevent
    // FeatureGate redirect flicker while the API call is in-flight.
    const [isLoaded, setIsLoaded] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) !== null;
        } catch {
            return false;
        }
    });

    const fetchFlags = useCallback(async () => {
        try {
            const response = await api.get<{ flags: string[] }>('/v1/features');
            const newFlags = response.data.flags;
            setFlags(newFlags);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newFlags));
            setIsLoaded(true);
        } catch (error) {
            console.error('Failed to fetch feature flags:', error);
            setIsLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (isAuthLoading) {
            // Auth state not yet determined — keep cached flags to avoid
            // FeatureGate redirect flicker (flags will refresh once auth resolves).
            return;
        }
        if (isAuthenticated) {
            fetchFlags();
        } else {
            // Confirmed unauthenticated (logout or no session) — clear flags.
            setFlags([]);
            localStorage.removeItem(STORAGE_KEY);
            setIsLoaded(true);
        }
    }, [isAuthenticated, isAuthLoading, fetchFlags]);

    const hasFlag = useCallback(
        (flagId: string) => flags.includes(flagId),
        [flags],
    );

    const hasAnyFlag = useCallback(
        (flagIds: string[]) => flagIds.some((id) => flags.includes(id)),
        [flags],
    );

    const value = useMemo(
        () => ({ flags, isLoaded, hasFlag, hasAnyFlag, refreshFlags: fetchFlags }),
        [flags, isLoaded, hasFlag, hasAnyFlag, fetchFlags],
    );

    return (
        <FeatureFlagContext.Provider value={value}>
            {children}
        </FeatureFlagContext.Provider>
    );
};
